import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, TextField, Button, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useService } from 'contexts/ServiceContextProvider';
import { useTranslation } from "react-i18next";
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import '../css/views/EditProject.css';
import '../css/main.css'
import CustomFallback from 'components/Custom/CustomFallBack';

const EditProjectPage = () => {

  const workspaceContext = useWorkspaces();
  const myWorkspaces = workspaceContext.workspaces;


  const [uploading, setUploading] = useState(false);
  const getProjectIDFromNavBar = () => {
    let currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    const isOnProjectEdit = currentPath.endsWith('/edit');
    let projectId;
    if (isOnProjectEdit) {
      projectId = pathParts[pathParts.length - 2];
    }
    return projectId;
  };



  const { t } = useTranslation();
  const navigate = useNavigate();


  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectImage, setProjectImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [projectOwner, setProjectOwner] = useState('');
  const [workspaceID, setWorkspaceID] = useState('');
  const [removeImage, setRemoveImage] = useState(false);
  const { UpdateExistingProject, getProjectInformation } = useService();
  const [visibility, setVisibility] = useState(false);
  const [status, setStatus] = useState('ongoing');
  const [project, setProject] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
  }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fetchProjectInformation = async (userPerms) => {
      const project_data = await getProjectInformation(myWorkspaces[0]?.id, getProjectIDFromNavBar());
      setProject(project_data);
      if (!project_data || !(userPerms?.is_admin || userPerms?.is_owner)) {
        navigate('/workflow/index');
      } else {
        setProjectName(project_data.name);
        setProjectDescription(project_data.desc);
        setPreviewImage(project_data.image ? project_data.image : null);
        setProjectOwner(project_data.owner);
        setWorkspaceID(project_data.workspace);
        setVisibility(project_data.visibility);
        setStatus(project_data.status); // Set the initial status
      }

    }
    if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0 && myWorkspaces[0]?.id) {
      const userPerms = workspaceContext.getLoggedUserPermissions();
      fetchProjectInformation(userPerms);
    }

  }, [myWorkspaces[0]?.id, workspaceContext.loading, workspaceContext.workspaceWorkers]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    document.title = "Editing " + projectName + " - Workflow PRO";
  }, [projectName]);

  const handleVisibilityChange = (event) => {
    setVisibility(event.target.checked);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setProjectImage(file);
    setRemoveImage(false);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetImage = () => {
    setProjectImage(null);
    setPreviewImage(null);
    setRemoveImage(true);
    document.getElementById("project-image-input").value = "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploading(true)

    try {
      const formData = new FormData();
      formData.append('name', projectName);
      formData.append('owner', projectOwner);
      formData.append('desc', projectDescription);
      formData.append('workspace', workspaceID);
      formData.append('visibility', visibility);
      formData.append('status', status); // Append status

      if (projectImage) {
        formData.append('image', projectImage);
      }
      if (removeImage) {
        formData.append('remove_image', 'true');
      }
      const response = await UpdateExistingProject(formData, project.id, workspaceID);

      if (response.status === 200) {
        window.location.href = `/workflow/index`;
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setUploading(false)
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return t('project.stat_completed');
      case 'ongoing':
        return t('project.stat_ongoing');
      case 'not_started':
        return t('project.stat_notstart');
      case 'archived':
        return t('project.stat_archived');
      default:
        return t('project.stat_notstart');

    }
  };

  // Only render the form if the project exists
  if (!project) {
    return null;
  }

  return (
    <Container>
      {uploading ? <CustomFallback /> :
        <>
          <Typography variant="h4" gutterBottom>{t('project.edit')}</Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('project.name')}
                  variant="outlined"
                  fullWidth
                  value={projectName}
                  onChange={(event) => setProjectName(event.target.value)}
                  InputLabelProps={{
                    className: "white-txt-color"
                  }}
                  inputProps={{
                    className: "white-txt-color"
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('project.desc')}
                  variant="outlined"
                  multiline
                  fullWidth
                  rows={4}
                  value={projectDescription}
                  onChange={(event) => setProjectDescription(event.target.value)}
                  InputLabelProps={{
                    className: "white-txt-color"
                  }}
                  InputProps={{
                    className: "white-txt-color"
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="image/*"
                  id="project-image-input"
                  type="file"
                  onChange={handleImageChange}
                  className="no-display"
                />
                <label htmlFor="project-image-input">
                  <Button variant="contained" component="span" className="button-light-bg">
                    {t('project.img')}
                  </Button>
                </label>
                <Typography>{t('project.rec_image_size')}</Typography>
                {previewImage && (
                  <div className="image-container">
                    <img src={previewImage} alt="Preview" className="preview-image-size image-border" />
                    <div className="overlay">
                      <button className="image-reset-button" onClick={resetImage}>{t("workspace.rem_image")}</button>
                    </div>
                  </div>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibility}
                      onChange={handleVisibilityChange}
                      color="primary"
                    />
                  }
                  label={t('project.public')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="standard" className="form-control-edit-project">
                  <InputLabel className="input-label-edit-project">
                    {t('global.status')}
                  </InputLabel>
                  <Select
                    value={status}
                    onChange={handleStatusChange}
                    MenuProps={{
                      PaperProps: {
                        className: "select-menu-edit-project",
                      },
                    }}
                  >
                    <MenuItem value="not_started">{t('project.stat_notstart')}</MenuItem>
                    <MenuItem value="ongoing">{t('project.stat_ongoing')}</MenuItem>
                    <MenuItem value="completed">{t('project.stat_completed')}</MenuItem>
                    <MenuItem value="archived">{t('project.stat_archived')}</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" className="current-stat-edit-project">
                  {t('project.current_stat')} {getStatusLabel(project.status)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button sx={{ mb: 2 }} type="submit" variant="contained" className="button-light-bg">
                  {t('global.save_changes')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </>}
    </Container>
  );
};

export default EditProjectPage;

